/**
 * Allowed uploads (direct S3 + app processing).
 * Keep in sync with backend `uploadConstraints.ts`.
 */

/** Must match backend `MAX_UPLOAD_FILE_SIZE_MB` in `uploadConstraints.ts`. */
export const MAX_UPLOAD_FILE_SIZE_MB = 50;
export const MAX_UPLOAD_FILE_SIZE_BYTES = MAX_UPLOAD_FILE_SIZE_MB * 1024 * 1024;

const ALLOWED_IMAGE_MIME = new Set([
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/heic',
  'image/heif',
]);

const ALLOWED_PDF_MIME = new Set(['application/pdf']);

const IMAGE_EXTENSIONS = new Set(['.jpg', '.jpeg', '.png', '.heic', '.heif']);

function extensionOf(name: string): string {
  const lower = name.toLowerCase();
  const dot = lower.lastIndexOf('.');
  return dot >= 0 ? lower.slice(dot) : '';
}

function normalizeType(file: File): string {
  return (file.type || '').split(';')[0].trim().toLowerCase();
}

function contentTypeMatchesExtension(ext: string, ct: string): boolean {
  if (!ct) {
    return true;
  }
  if (ext === '.pdf') {
    return ALLOWED_PDF_MIME.has(ct);
  }
  if (ext === '.jpg' || ext === '.jpeg') {
    return ct === 'image/jpeg' || ct === 'image/jpg';
  }
  if (ext === '.png') {
    return ct === 'image/png';
  }
  if (ext === '.heic' || ext === '.heif') {
    return ct === 'image/heic' || ct === 'image/heif';
  }
  return false;
}

/** HTML file input accept list */
export const UPLOAD_INPUT_ACCEPT = [
  'image/jpeg',
  'image/png',
  'image/heic',
  'image/heif',
  'application/pdf',
  '.jpg',
  '.jpeg',
  '.png',
  '.heic',
  '.heif',
  '.pdf',
].join(',');

/**
 * True if the file is a supported image or PDF (extension + MIME consistent when type is present).
 */
export function isAllowedUploadFile(file: File): boolean {
  const name = file.name || '';
  const ext = extensionOf(name);
  const ct = normalizeType(file);

  if (ext === '.pdf') {
    if (ct && !ALLOWED_PDF_MIME.has(ct)) {
      return false;
    }
    return true;
  }

  if (IMAGE_EXTENSIONS.has(ext)) {
    if (ct && !ALLOWED_IMAGE_MIME.has(ct)) {
      return false;
    }
    return contentTypeMatchesExtension(ext, ct);
  }

  return false;
}

/**
 * User-facing list for errors and help text.
 */
export const SUPPORTED_UPLOAD_TYPES_LABEL = 'JPG, PNG, HEIC, or PDF';
